import moment from "moment";
import { FC, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import db from "src/db";
import { toAbsoluteUrl } from "src/resources/helpers";
import { CapitalizedLetter } from "src/util/Formatter";
import { downloadInvoiceAsPDF } from "./DownloadInvoice";

type InvoicePreviewModalProps = {
  open: boolean;
  close?: () => void;
  marketplaceType: string;
  downloadInv?: () => void;
  data?: any;
};

export const InvoicePreviewModal: FC<InvoicePreviewModalProps> = ({
  open,
  close,
  marketplaceType,
  downloadInv,
  data,
}) => {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 900px)" });
  const [accountData, setAccountData] = useState<any>();
  const [shippingLabelError, setShippingLabelError] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { t } = useTranslation();
  const fileUrl = data?.shippingLabelFile?.url ?? "";
  const totalPages = 2;

  const orderStatus: { [key: string]: string } = {
    NEW_ORDER: "OrderStatus7Days.NewOrder",
    PROCESSED: "OrderStatus7Days.Processing",
    COMPLETED: "OrderStatus7Days.Completed",
    CANCELLED: "OrderStatus7Days.Cancelled",
    CANCELED: "OrderStatus7Days.Cancelled",
    READY_TO_SHIP: "OrderStatus7Days.ReadyToShip",
    UNPAID: "OrderStatus7Days.Unpaid",
    SHIPPING: "OrderStatus7Days.Shipping",
    RETURN: "OrderStatus7Days.Return",
    DELIVER: "OrderStatus7Days.Delivered",
    DELIVERED: "OrderStatus7Days.Delivered",
  };

  const statusColor: { [key: string]: string } = {
    NEW_ORDER: "primary",
    PROCESSED: "warning",
    COMPLETED: "success",
    CANCELLED: "danger",
    CANCELED: "danger",
    READY_TO_SHIP: "success",
    UNPAID: "secondary",
    SHIPPING: "warning",
    RETURN: "secondary",
    DELIVER: "success",
    DELIVERED: "success",
  };

  let orderedItems: Array<any> = data?.itemList;

  useEffect(() => {
    const getAccountData = async () => {
      let idAccount = data?.account?.id;
      if (idAccount) {
        let acc = await db
          .collection("account")
          .doc(idAccount)
          .get()
          .then((doc) => doc.exists && doc.data());
        setAccountData(acc);
      }
    };
    getAccountData();
  }, []);

  const formatNumber = (number: number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const downloadShippingLabel = () => {
    if (fileUrl) {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `shipping_label_${data.ordersn}.pdf`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePdfError = () => {
    setShippingLabelError(true);
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  let totalAmount = 0;
  orderedItems?.forEach((item) => {
    let price: number;
    if (item.discountedPrice) {
      price = item.discountedPrice;
    } else {
      if (item.originalPrice) {
        price = item.originalPrice;
      } else {
        price = item.priceActual;
      }
    }
    totalAmount += price * item.qtyOrderItem;
  });

  // Shipping Label Page Component
  const ShippingLabelPage = () => (
    <div
      className="bg-white p-0"
      style={{
        borderTopWidth: "5px",
        borderTopStyle: "solid",
        borderColor:
          marketplaceType === "tokopedia"
            ? "#4F9D4E"
            : marketplaceType === "shopee"
            ? "#ee4d2d"
            : marketplaceType === "lazada"
            ? "#000083"
            : "gray",
      }}
    >
      {/* Shipping Label Content */}
      <div className="d-flex flex-row w-100 align-items-start justify-content-between px-5 py-5">
        <div className="d-flex flex-column w-100">
          {fileUrl && !shippingLabelError ? (
            <div className="shipping-label-preview">
              <div className="d-flex flex-column align-items-center">
                <iframe
                  src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  width="100%"
                  height="500px"
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    marginBottom: "10px",
                  }}
                  title="Shipping Label Preview"
                  onError={handlePdfError}
                />
              </div>
            </div>
          ) : (
            <div
              className="d-flex flex-column align-items-center justify-content-center p-5 bg-light border rounded"
              style={{ height: "500px" }}
            >
              <i className="bi bi-file-earmark-x fs-1 text-muted mb-3"></i>
              <p className="text-muted mb-0">
                {fileUrl && shippingLabelError
                  ? t("HandledCustomer.Modal.Invoice.ShippingLabelError")
                  : t("HandledCustomer.Modal.Invoice.ShippingLabelNotFound")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Invoice Page Component
  const InvoicePage = () => (
    <div
      className="bg-white p-0"
      style={{
        borderTopWidth: "5px",
        borderTopStyle: "solid",
        borderColor:
          marketplaceType === "tokopedia"
            ? "#4F9D4E"
            : marketplaceType === "shopee"
            ? "#ee4d2d"
            : marketplaceType === "lazada"
            ? "#000083"
            : "gray",
      }}
    >
      {/* Header with Order Info */}
      <div className="d-flex flex-wrap-reverse flex-row w-100 align-items-end justify-content-between px-5 py-5">
        <div className="d-flex flex-column m-0 p-0 align-items-start justify-content-start">
          <div className="d-flex flex-wrap flex-row col-gap-1 align-items-center justify-content-start mb-3">
            <p className="fw-bolder fs-5 m-0 p-0">{data.ordersn}</p>
            <span
              className={`m-0 p-0 bg-${
                statusColor[
                  marketplaceType === "lazada"
                    ? data.marketplace_status
                    : data.status
                ]
              } px-2 py-1 rounded text-white fs-9`}
            >
              {t(
                orderStatus[
                  marketplaceType === "lazada"
                    ? data.marketplace_status
                    : data.status
                ]
              )}
            </span>
          </div>
          <p className="m-0 p-0 text-start">
            <span style={{ width: "120px", display: "inline-block" }}>
              {t("HandledCustomer.Modal.Invoice.OrderCreated")}
            </span>
            :{" "}
            {moment(
              new Date(data?.marketplaceOrderCreatedAt?.seconds * 1000)
            ).format("DD/MM/YYYY")}
          </p>
          {data?.shippingTrackingNumber && (
            <p className="pt-0 mt-0 pb-0 mb-0 text-start">
              <span style={{ width: "120px", display: "inline-block" }}>
                {t("HandledCustomer.Modal.Invoice.TrackingNumber")}
              </span>
              : {data?.shippingTrackingNumber}
            </p>
          )}
        </div>
        <div className="w-auto d-flex align-items-start justify-content-end m-0 p-0">
          <img
            className="w-50px"
            src={toAbsoluteUrl(
              `/media/logos/marketplace/${marketplaceType.toLowerCase()}.png`
            )}
            style={{
              objectFit: "contain",
              width: "100%",
              height: "100%",
              margin: 0,
              padding: 0,
            }}
          />
        </div>
      </div>

      <div className="border-bottom border-1 border-secondary"></div>

      {/* From/To Section */}
      <div className="d-flex flex-wrap flex-row w-100 align-items-start justify-content-between px-5 py-5">
        <div className="d-flex flex-column w-50">
          <p
            className="mb-0 pb-2 fw-bolder text-start"
            style={{ color: "#979797" }}
          >
            {t("Notif.info.From")}
          </p>
          <p
            className="mb-0 pb-0 text-start"
            style={{ wordBreak: "break-word" }}
          >
            {CapitalizedLetter(marketplaceType)}
          </p>
          {accountData && (
            <p
              className="mb-0 pb-0 text-start"
              style={{ wordBreak: "break-word" }}
            >
              {CapitalizedLetter(accountData.name)}
            </p>
          )}
        </div>
        <div className="d-flex flex-column w-50">
          <p
            className="mb-0 pb-2 fw-bolder text-start"
            style={{ color: "#979797" }}
          >
            {t("Notif.info.To")}
          </p>
          <p
            className="mb-0 pb-0 flex-wrap text-start"
            style={{ wordBreak: "break-word" }}
          >
            {CapitalizedLetter(data?.buyerName)}
          </p>
          <p
            className="mb-0 pb-0 text-start"
            style={{ wordBreak: "break-word" }}
          >
            {data?.shippingAddress?.detailAddress},
            {data?.shippingAddress?.district}
          </p>
          <p
            className="mb-0 pb-0 flex-wrap text-start"
            style={{ wordBreak: "break-word" }}
          >
            {data?.shippingAddress?.city}, {data?.shippingAddress?.state},
            {data?.shippingAddress?.postalCode}
          </p>
          <p
            className="mb-0 pb-0 text-start"
            style={{ wordBreak: "break-word" }}
          >
            {data?.receiverPhone}
          </p>
        </div>
      </div>

      <div className="border-bottom border-1 border-secondary"></div>

      {/* Shipper Section */}
      <div className="d-flex flex-row w-100 align-items-start justify-content-between px-5 py-5">
        <div className="d-flex flex-column w-50">
          <p className="fw-bolder fs-5 mb-2 p-0 text-start">
            {t("HandledCustomer.Modal.Invoice.Shipper")}
          </p>
          <p className="mb-0 pb-0 text-start">
            {data?.marketplaceDeliveryChannelParent ||
              data?.marketplaceLogisticChannelParent ||
              data?.marketplaceDeliveryChannelName}{" "}
            - {data?.shippingLabel}
          </p>
        </div>
      </div>

      <div className="border-bottom border-1 border-secondary"></div>

      {/* Items Section */}
      <div className="d-flex flex-row w-100 align-items-start justify-content-between px-5 py-5">
        <div className="d-flex flex-column w-100">
          <p className="fw-bolder fs-5 mb-2 p-0 text-start">
            {t("HandledCustomer.Modal.Invoice.ItemsOrder")}
          </p>
          <div className="d-flex w-100 col-gap-1 flex-row mb-1 pb-0">
            <p
              className="mb-0 pb-0 fw-bolder text-start"
              style={{ color: "#979797", width: "60%" }}
            >
              {t("HandledCustomer.Modal.Invoice.Product")}
            </p>
            <p
              className="mb-0 pb-0 text-center fw-bolder"
              style={{ color: "#979797", width: "10%" }}
            >
              {t("HandledCustomer.Modal.Invoice.Quantity")}
            </p>
            <p
              className="mb-0 pb-0 text-center fw-bolder"
              style={{ color: "#979797", width: "20%" }}
            >
              {t("HandledCustomer.Modal.Invoice.UnitPrice")}
            </p>
            <p
              className="mb-0 pb-0 text-end fw-bolder"
              style={{ color: "#979797", width: "20%" }}
            >
              {t("HandledCustomer.Modal.Invoice.SubTotal")}
            </p>
          </div>
          {orderedItems?.length &&
            orderedItems?.map((item: any, index: number) => {
              let price: number;
              if (item.discountedPrice) {
                price = item.discountedPrice;
              } else {
                if (item.originalPrice) {
                  price = item.originalPrice;
                } else {
                  price = item.priceActual;
                }
              }
              return (
                <div
                  className={`d-flex w-100 col-gap-1 flex-row py-1 border-bottom border-1 border-secondary`}
                  key={index}
                >
                  <p className="mb-0 pb-0 text-start" style={{ width: "60%" }}>
                    {item?.itemName}
                  </p>
                  <p className="mb-0 pb-0 text-center" style={{ width: "10%" }}>
                    {item?.qtyOrderItem}
                  </p>
                  <p className="mb-0 pb-0 text-center" style={{ width: "20%" }}>
                    Rp {formatNumber(price)}
                  </p>
                  <p className="mb-0 pb-0 text-end" style={{ width: "20%" }}>
                    Rp {formatNumber(price * item?.qtyOrderItem)}
                  </p>
                </div>
              );
            })}

          <div className="d-flex w-100 col-gap-1 flex-row mt-1">
            <p className="mb-0 pb-0" style={{ width: "60%" }}></p>
            <p className="mb-0 pb-0" style={{ width: "10%" }}></p>
            <p
              className="mb-0 pb-0 text-center fw-bolder"
              style={{ width: "20%" }}
            >
              {t("HandledCustomer.RoomChat.GranTot")}
            </p>
            <p
              className="mb-0 pb-0 text-end fw-bolder"
              style={{ width: "20%" }}
            >
              Rp {formatNumber(totalAmount)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      animation
      centered
      show={open}
      onHide={() => close && close()}
      scrollable={false}
      size="lg"
      dialogClassName={!isTabletOrMobile ? "w-550px h-fit" : "h-fit"}
      style={{
        zIndex: "99999",
      }}
    >
      <Modal.Header closeButton className="bg-light p-5">
        <Modal.Title data-testid="modal-title">
          {currentPage === 1
            ? t("HandledCustomer.Modal.Invoice.ShippingLabel")
            : "Invoice"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light p-3 pt-0" id="invoice-modal-content">
        {/* Simple Navigation */}
        <div className="d-flex w-full flex-row justify-content-between align-items-center mb-3 ">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${
                currentPage === 1 ? "btn-primary" : "btn-secondary"
              }`}
              onClick={() => goToPage(1)}
            >
              <i className="bi bi-tag me-1"></i>
              Label
            </button>
            <button
              type="button"
              className={`btn ${
                currentPage === 2 ? "btn-primary" : "btn-secondary"
              }`}
              onClick={() => goToPage(2)}
            >
              <i className="bi bi-receipt me-1"></i>
              Invoice
            </button>
          </div>
          <div>
            {currentPage === 1 ? (
              // Shipping Label Page Footer
              <button
                className="btn btn-primary btn-sm"
                onClick={downloadShippingLabel}
                disabled={!fileUrl}
              >
                <i className="bi bi-download m-0 p-0"></i>
              </button>
            ) : (
              // Invoice Page Footer
              <button
                className="btn btn-primary btn-sm"
                onClick={() => downloadInvoiceAsPDF(data)}
              >
                <i className="bi bi-download m-0 p-0"></i>
              </button>
            )}
          </div>
        </div>

        {/* Page Content */}
        {currentPage === 1 ? <ShippingLabelPage /> : <InvoicePage />}
      </Modal.Body>
    </Modal>
  );
};
